import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Heart, ArrowLeft, MessageCircle, Edit2, Save, X, Upload, Camera, Image as ImageIcon, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import API_URL from '../../utils/apiUrl';
import { t } from '../../i18n/t';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

// Talk sites are chat sites: no dating heart.
const BrandMark = process.env.REACT_APP_MODE === 'talk' ? MessageCircle : Heart;

const DatingProfile = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user, authLoading, getAuthHeader, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editData, setEditData] = useState({});
  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePassword, setClosePassword] = useState('');
  const [closing, setClosing] = useState(false);

  const isOwnProfile = !userId || userId === user?.id;

  const handleCloseAccount = async () => {
    setClosing(true);
    try {
      await axios.post(`${API_URL}/api/auth/close-account`, { password: closePassword },
        { headers: getAuthHeader() });
      toast.success(t('closeAccountDone'));
      logout();
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('closeAccountFailed'));
    }
    setClosing(false);
  };

  useEffect(() => {
    fetchProfile();
  }, [userId, user, authLoading]);

  const fetchProfile = async () => {
    const targetId = userId || user?.id;
    if (!targetId) {
      // Still waiting for the session to resolve? keep the spinner. Otherwise
      // stop loading so the user sees a real message instead of spinning forever.
      if (!authLoading) setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/profiles/${targetId}`, {
        headers: getAuthHeader()
      });
      setProfile(response.data);
      setEditData({
        username: response.data.username || '',
        age: response.data.age || '',
        bio: response.data.bio || '',
        main_avatar: response.data.main_avatar || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error(error?.response?.data?.detail || t('failedLoadProfile'));
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${API_URL}/api/profiles/${user?.id}`,
        {
          username: editData.username || undefined,
          age: editData.age ? parseInt(editData.age) : undefined,
          bio: editData.bio || undefined,
          main_avatar: editData.main_avatar || undefined
        },
        { headers: getAuthHeader() }
      );
      setProfile(response.data);
      setEditing(false);
      toast.success(t('profileUpdated'));
    } catch (error) {
      toast.error(t('failedUpdateProfile'));
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('selectImageFile'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_avatar', 'true');
    formData.append('is_private', 'false');

    try {
      const response = await axios.post(
        `${API_URL}/api/upload/profile-image/${user?.id}`,
        formData,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update local state
      setEditData({ ...editData, main_avatar: response.data.url });
      setProfile({ ...profile, main_avatar: response.data.url });
      toast.success(t('avatarUploaded'));
      fetchProfile(); // Refresh to get updated gallery
    } catch (error) {
      toast.error(t('failedUploadAvatar'));
    }
    setUploading(false);
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('selectImageFile'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_avatar', 'false');
    formData.append('is_private', 'false');

    try {
      await axios.post(
        `${API_URL}/api/upload/profile-image/${user?.id}`,
        formData,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(t('photoAdded'));
      fetchProfile(); // Refresh gallery
    } catch (error) {
      toast.error(t('failedUploadPhoto'));
    }
    setUploading(false);
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm(t('deletePhotoConfirm'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/gallery/${imageId}`, {
        headers: getAuthHeader()
      });
      toast.success(t('photoDeleted'));
      fetchProfile();
    } catch (error) {
      toast.error(t('failedDeletePhoto'));
    }
  };

  const defaultAvatar = 'https://images.unsplash.com/photo-1593704345154-791a1430ac8f?w=400&h=400&fit=crop';

  if (loading) {
    return (
      <div className="min-h-screen dating-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen dating-bg flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="profile-unavailable">
        <p className="text-base text-slate-700">
          {user ? t('profileNotFound') : t('mustLoginProfile')}
        </p>
        <Button onClick={() => navigate(user ? '/discover' : '/login')} data-testid="profile-unavailable-action">
          {user ? t('backToProfiles') : t('login')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen dating-bg font-dating-body">
      {/* Hidden file inputs */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarUpload}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleGalleryUpload}
      />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-rose-100 px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <BrandMark className="h-6 w-6 text-rose-500 fill-rose-500" />
          <span className="font-dating-heading text-lg font-bold text-slate-800">
            {isOwnProfile ? t('myProfile') : t('profile')}
          </span>
        </div>
        {isOwnProfile && !editing && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto"
            onClick={() => setEditing(true)}
            data-testid="edit-btn"
          >
            <Edit2 className="h-5 w-5" />
          </Button>
        )}
        {isOwnProfile && editing && (
          <div className="ml-auto flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditing(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              className="bg-rose-500 hover:bg-rose-600 text-white"
              onClick={handleSave}
              data-testid="save-btn"
            >
              <Save className="h-5 w-5" />
            </Button>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="border-0 shadow-xl overflow-hidden">
          {/* Cover Photo Area */}
          <div className="h-32 bg-gradient-to-r from-rose-400 to-rose-500" />
          
          {/* Avatar */}
          <div className="relative px-6">
            <div className="relative -mt-12">
              <Avatar className="w-24 h-24 ring-4 ring-white">
              <AvatarImage src={(() => {
                const av = profile?.main_avatar || defaultAvatar;
                return av.startsWith('http') ? av : `${API_URL}${av}`;
              })()} />
                <AvatarFallback className="text-2xl">{profile?.username?.[0] || '?'}</AvatarFallback>
              </Avatar>
              {isOwnProfile && (
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 p-2 bg-rose-500 rounded-full text-white shadow-lg hover:bg-rose-600 transition-colors"
                  data-testid="change-avatar-btn"
                >
                  <Camera className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <CardContent className="pt-4 pb-6">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="avatar">{t('profilePhotoUrl')}</Label>
                  <Input
                    id="avatar"
                    value={editData.main_avatar}
                    onChange={(e) => setEditData({ ...editData, main_avatar: e.target.value })}
                    placeholder="https://..."
                    data-testid="edit-avatar"
                  />
                </div>
                <div>
                  <Label htmlFor="username">{t('username')}</Label>
                  <Input
                    id="username"
                    value={editData.username}
                    onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                    data-testid="edit-username"
                  />
                </div>
                <div>
                  <Label htmlFor="age">{t('age')}</Label>
                  <Input
                    id="age"
                    type="number"
                    value={editData.age}
                    onChange={(e) => setEditData({ ...editData, age: e.target.value })}
                    data-testid="edit-age"
                  />
                </div>
                <div>
                  <Label htmlFor="bio">{t('aboutMe')}</Label>
                  <Textarea
                    id="bio"
                    value={editData.bio}
                    onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                    rows={4}
                    placeholder={t('tellUsAbout')}
                    data-testid="edit-bio"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="font-dating-heading text-2xl font-semibold text-slate-800">
                      {profile?.username || 'Anonymous'}
                      {profile?.age && <span className="text-slate-500 font-normal">, {profile.age}</span>}
                    </h1>
                    {profile?.email && isOwnProfile && (
                      <p className="text-sm text-slate-500 mt-1">{profile.email}</p>
                    )}
                  </div>
                  {!isOwnProfile && (
                    <Button
                      className="dating-primary-btn"
                      onClick={() => navigate(`/chat/${profile?.id}`)}
                      data-testid="message-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      {t('messageButton')}
                    </Button>
                  )}
                </div>

                {profile?.bio && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-slate-500 mb-2">{t('about')}</h3>
                    <p className="text-slate-700">{profile.bio}</p>
                  </div>
                )}

                {/* Gallery */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-500">{t('photos')}</h3>
                    {isOwnProfile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="add-photo-btn"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        {uploading ? t('uploading') : t('addPhoto')}
                      </Button>
                    )}
                  </div>
                  
                  {profile?.gallery && profile.gallery.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {profile.gallery.map((img) => {
                        const imgSrc = img.image_url.startsWith('http') ? img.image_url : `${API_URL}${img.image_url}`;
                        return (
                        <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img
                            src={imgSrc}
                            alt="Gallery"
                            className="w-full h-full object-cover"
                          />
                          {isOwnProfile && (
                            <button
                              onClick={() => handleDeleteGalleryImage(img.id)}
                              className="absolute top-1 right-1 p-1.5 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                          {img.is_private && (
                            <span className="absolute bottom-1 left-1 text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">
                              {t('private')}
                            </span>
                          )}
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <ImageIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">{t('noPhotos')}</p>
                      {isOwnProfile && (
                        <Button
                          variant="link"
                          className="text-rose-500 mt-1"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {t('addFirstPhoto')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {isOwnProfile && (
          <div className="mt-10 mb-6 text-center">
            <button
              onClick={() => setCloseOpen(true)}
              className="text-[11px] text-slate-300 hover:text-slate-500 transition-colors"
              data-testid="close-account-link"
            >
              {t('closeAccount')}
            </button>
          </div>
        )}

        <Dialog open={closeOpen} onOpenChange={(o) => { setCloseOpen(o); if (!o) setClosePassword(''); }}>
          <DialogContent data-testid="close-account-dialog">
            <DialogHeader>
              <DialogTitle>{t('closeAccountTitle')}</DialogTitle>
              <DialogDescription>{t('closeAccountInfo')}</DialogDescription>
            </DialogHeader>
            <div>
              <Label>{t('closeAccountPassword')}</Label>
              <Input
                type="password"
                value={closePassword}
                onChange={(e) => setClosePassword(e.target.value)}
                data-testid="close-account-password"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setCloseOpen(false)} data-testid="close-account-cancel">
                {t('closeAccountKeep')}
              </Button>
              <Button
                variant="destructive"
                disabled={closing || !closePassword}
                onClick={handleCloseAccount}
                data-testid="close-account-confirm"
              >
                {closing ? '...' : t('closeAccountConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default DatingProfile;
